"use client";

import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { getFormsCopy } from "@/content/forms";
import { useLocale } from "@/lib/i18n/provider";

/**
 * Lightweight, accessible modal.
 *
 * - Portals into `<body>` so it escapes section stacking contexts.
 * - Locks scroll while open.
 * - Closes on `Escape`, backdrop click, or the close button.
 * - Returns focus to the trigger on close (caller responsibility:
 *   `Modal` only focuses the close button when opened).
 *
 * The Modal is unstyled for content — callers compose their own
 * heading + body + CTA inside.
 */

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  size?: "md" | "lg";
};

export function Modal({ open, onClose, title, description, children, size = "md" }: Props) {
  const { locale } = useLocale();
  const forms = getFormsCopy(locale);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleClose]);

  if (typeof document === "undefined") return null;

  const widthClass = size === "lg" ? "max-w-2xl" : "max-w-md";

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-end justify-center px-4 pb-6 pt-12 sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          aria-hidden={!open}
        >
          <button
            type="button"
            aria-label={forms.primitive.modalCloseDialogLabel}
            onClick={handleClose}
            className="absolute inset-0 cursor-default bg-[var(--modal-backdrop-bg)] backdrop-blur-md"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            aria-describedby={description ? "modal-desc" : undefined}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 360, damping: 32 }}
            className={`relative w-full ${widthClass} overflow-hidden rounded-3xl border border-[var(--color-surface-border-strong)] bg-[var(--color-bg-raised)] p-6 shadow-[var(--modal-shadow)] sm:p-8`}
          >
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h2 id="modal-title" className="text-xl font-semibold tracking-tight text-[var(--color-text-primary)]">
                  {title}
                </h2>
                {description && (
                  <p id="modal-desc" className="mt-1 text-sm text-[var(--color-text-secondary)]">
                    {description}
                  </p>
                )}
              </div>
              <button
                ref={closeBtnRef}
                type="button"
                onClick={handleClose}
                aria-label={forms.primitive.modalCloseLabel}
                className="-m-2 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-glass)] hover:text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-base)]"
              >
                <X size={18} />
              </button>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
